import React, { useState } from "react";
import {
Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Tooltip,
    CircularProgress,
} from "@mui/material";
import BackupOutlinedIcon from "@mui/icons-material/BackupOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useDropzone } from "react-dropzone";
import { StyledPaper, StyledIcon, ListStyle, FileItemContainer, FileNameText } from "./MultiDropZoneDialogStyle";
import { Button } from "@material-ui/core";


const FileListItem = ({ file, index, onRemove, uploadDone }) => (
    <ListItem
        key={file.path || file.name + index}
        secondaryAction={!uploadDone &&
            <IconButton onClick={() => onRemove(index)}>
                <CloseOutlinedIcon />
            </IconButton>
        }
    >
        <ListItemText
            primary={
                <div style={FileItemContainer}>
                    <Typography variant="body1" sx={FileNameText}>
                        {file.name}
                    </Typography>
                    {file.status && (
                        <Tooltip title={file.status === "error" ? file.message : "Uploaded successfully"}>
                            {file.status === "success" ? (
                                <CheckCircleOutlineIcon sx={{ color: "green" }} />
                            ) : (
                                <ErrorOutlineIcon sx={{ color: "red" }} />
                            )}
                        </Tooltip>
                    )}
                </div>
            }
        />
    </ListItem>
);


const DropZoneArea = ({ getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject, dropMessage }) => (
    <StyledPaper {...getRootProps()}>
        <BackupOutlinedIcon sx={StyledIcon} />
        <input {...getInputProps()} />
        {isDragAccept && <Typography>All files will be accepted</Typography>}
        {isDragReject && <Typography>Some files will be rejected</Typography>}
        {!isDragActive && <Typography>{dropMessage}</Typography>}
    </StyledPaper>
);


const MultiDropZoneDialog = ({
    open,
    onClose,
    onSave,
    id,
    labelDialog,
    formContent,
    dropMessage = "Drag and drop files here, or click to select files",
    maxFiles = 10,
    accept = { "text/csv": [".csv"] },
    actionButtonLabel = "Upload",
    valid = true,
}) => {
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadDone, setUploadDone] = useState(false);

    const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
        onDrop: (acceptedFiles) => {
            const filteredFiles = acceptedFiles.filter(
                (file) => !selectedFiles.some((f) => f.name === file.name && f.size === file.size)
            );
            setSelectedFiles((prev) => [...prev, ...filteredFiles].slice(0, maxFiles));
        },
        accept,
        maxFiles,
    });

    const handleRemoveFile = (index) => setSelectedFiles((prev) => prev.filter((_, i) => i !== index));


    const handleClose = () => {
        setSelectedFiles([]);
        setUploadDone(false);
        onClose?.();
    };

    const handleSave = async (filesToUpload = selectedFiles) => {
        if (!onSave || filesToUpload.length === 0) return;

        setIsUploading(true);
        const results = await onSave(filesToUpload);
        const filesResults = results?.files || [];

        setSelectedFiles((prev) =>
            prev.map((file) => {
                const result = filesResults.find((r) => r.fileName === file.name);
                return result
                    ? {
                        file,
                        status: result.success ? "success" : "error",
                        message: result.error || "",
                        name: result.fileName
                    }
                    : file;
            })
        );
        setIsUploading(false);
        setUploadDone(true);

    };

    const hasFailed = selectedFiles.some((f) => f.status === "error");
    const allFinished = selectedFiles.every((f) => f.status);

    return (
        <>
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>{labelDialog}</DialogTitle>
                <DialogContent>
                    {formContent}
                    {!uploadDone && (
                        <DropZoneArea
                            getRootProps={getRootProps}
                            getInputProps={getInputProps}
                            isDragActive={isDragActive}
                            isDragAccept={isDragAccept}
                            isDragReject={isDragReject}
                            dropMessage={dropMessage}
                        />
                    )}

                    {selectedFiles.length > 0 && (
                        <List sx={ListStyle}>
                            {selectedFiles.map((file, index) => (
                                <FileListItem key={file.name + index} file={file} index={index} onRemove={handleRemoveFile} uploadDone={uploadDone} />
                            ))}
                        </List>
                    )}
                </DialogContent>
                <DialogActions>
                    {!allFinished ? (
                        <>
                            <Button onClick={handleClose} color="primary" disabled={isUploading}>Cancel</Button>
                            <Button
                                id={id}
                                variant="contained"
                                color="primary"
                                onClick={() => handleSave()}
                                disabled={selectedFiles.length === 0 || !valid || isUploading}
                                startIcon={isUploading ? <CircularProgress size={18} /> : null}
                            >
                                {isUploading ? "Uploading..." : actionButtonLabel}
                            </Button>
                        </>
                    ) : hasFailed ? (
                        <>
                            <Button onClick={handleClose} color="primary">Close</Button>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() =>{
                                const failed = selectedFiles.filter(f => f.status === "error").map(f => f.file);
                                setSelectedFiles(failed)
                                handleSave(failed)
                            }
                                }
                            >
                                Reupload Failed
                            </Button>
                        </>
                    ) : (
                        <Button onClick={handleClose} color="primary" variant="contained">Close</Button>
                    )}
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MultiDropZoneDialog;
